export type Language = 'en' | 'ar';

type CaseStudyCopy = {
  name: string;
  category: string;
  market: string;
  title: string;
  summary: string;
  overview: string;
  screenshotAlt: string;
  screenshotCaption?: string;
  featuresHeading?: string;
  features: { title: string; description: string }[];
};

export type CaseStudy = {
  slug: string;
  website: string;
  icon: string;
  screenshot: string;
  imageLayout?: 'website';
  apps: { audience: 'customer' | 'provider'; url: string }[];
  copy: Record<Language, CaseStudyCopy>;
};

// Add a reviewed entry here to publish its homepage card and dedicated page.
// Sources: https://www.maksab.sd/ and the Google Play listings below.
export const caseStudies: CaseStudy[] = [{
  slug: 'maksab',
  website: 'https://www.maksab.sd/',
  icon: '/case-studies/maksab-icon.png',
  screenshot: '/case-studies/maksab-app.jpg',
  apps: [
    { audience: 'customer', url: 'https://play.google.com/store/apps/details?id=sd.maksab.customer' },
    { audience: 'provider', url: 'https://play.google.com/store/apps/details?id=sd.maksab.provider' },
  ],
  copy: {
    en: {
      name: 'Maksab', category: 'Home services', market: 'Sudan',
      screenshotAlt: 'Maksab customer app showing maintenance, building, and cleaning services',
      title: 'Everyday services, connected in Sudan.',
      summary: 'Meet Maksab: a marketplace connecting people in Sudan with local service providers for maintenance, cleaning, and everyday needs.',
      overview: 'Maksab brings service discovery and requests into one mobile experience. Customers can explore providers and their previous work, discuss what they need, and rate the service after completion. A separate provider app supports the other side of the marketplace.',
      features: [
        { title: 'Find the right service', description: 'Browse home maintenance, cleaning, moving, and other everyday services.' },
        { title: 'Get to know the provider', description: 'Review provider profiles, ratings, and previous work before making a request.' },
        { title: 'Keep the conversation together', description: 'Discuss the job with the provider through chat and share pictures to explain what is needed.' },
        { title: 'Manage incoming work', description: 'Providers use their own app to receive requests, accept or reject them, and complete orders after the work is done.' },
      ],
    },
    ar: {
      name: 'مكسب', category: 'الخدمات المنزلية', market: 'السودان',
      screenshotAlt: 'تطبيق مكسب للعملاء يعرض خدمات الصيانة والبناء والنظافة',
      title: 'خدمات الحياة اليومية في السودان، في مكان واحد.',
      summary: 'تعرّف على مكسب: سوق يربط العملاء في السودان بمقدمي الخدمات المحليين للصيانة والنظافة والاحتياجات اليومية.',
      overview: 'يجمع مكسب استكشاف الخدمات وطلبها في تجربة واحدة عبر الجوال. يستطيع العملاء تصفح مقدمي الخدمات وأعمالهم السابقة، ومناقشة احتياجاتهم، وتقييم الخدمة بعد إنجازها. ويتيح تطبيق مستقل لمقدمي الخدمات إدارة جانبهم من السوق.',
      features: [
        { title: 'اعثر على الخدمة المناسبة', description: 'تصفّح خدمات صيانة المنازل والنظافة والنقل وغيرها من الخدمات اليومية.' },
        { title: 'تعرّف على مقدم الخدمة', description: 'اطّلع على الملفات الشخصية والتقييمات والأعمال السابقة قبل إرسال طلبك.' },
        { title: 'تواصل حول تفاصيل الطلب', description: 'ناقش العمل مع مقدم الخدمة عبر المحادثة وشارك الصور لتوضيح احتياجاتك.' },
        { title: 'أدر طلبات العمل', description: 'يستخدم مقدمو الخدمات تطبيقهم لاستقبال الطلبات وقبولها أو رفضها، ثم إكمالها بعد تنفيذ العمل.' },
      ],
    },
  },
}, {
  slug: 'sogo',
  website: `https://sogo.${import.meta.env.VITE_PLATFORM_DOMAIN?.trim() || 'localhost'}/ar`,
  icon: '/case-studies/sogo-mark.svg',
  screenshot: '/case-studies/sogo-platform.png',
  imageLayout: 'website',
  apps: [],
  copy: {
    en: {
      name: 'SOGO', category: 'E-commerce platform', market: 'Sudan',
      title: 'An e-commerce platform for Sudan’s merchants.',
      summary: 'SOGO gives merchants their own online stores, with tools to manage products, orders, stock and customers. Built with Heglo for retail and wholesale businesses in Sudan.',
      overview: 'SOGO is a commerce startup built on Heglo. Each merchant has a separate storefront to share with customers and a business portal to run daily operations. The platform supports different product categories and selling models, including wholesale ordering for business buyers.',
      screenshotAlt: 'SOGO’s Arabic website showing its purple branding, merchant dashboard and storefront preview',
      screenshotCaption: 'SOGO website · DEV preview',
      featuresHeading: 'From the merchant’s catalog to the customer’s order.',
      features: [
        { title: 'A store for each merchant', description: 'Merchants share their own storefront link, with their branding, categories and product pages.' },
        { title: 'Products and inventory', description: 'Manage product options, prices and stock quantities to suit the business, from clothing sizes to grocery packs.' },
        { title: 'Retail and wholesale', description: 'Configure business-buyer approval, minimum orders and pricing tiers for wholesale sales.' },
        { title: 'Daily store operations', description: 'Follow orders, customers, returns and operational sales reports from the merchant portal.' },
      ],
    },
    ar: {
      name: 'سوجو', category: 'منصة للتجارة الإلكترونية', market: 'السودان',
      title: 'منصة للتجارة الإلكترونية في السودان.',
      summary: 'تمنح سوجو كل تاجر متجره الإلكتروني وأدوات لإدارة المنتجات والطلبات والمخزون والعملاء. منصة مبنية على هيجلو لتجارة التجزئة والجملة في السودان.',
      overview: 'سوجو مشروع تقني للتجارة مبني على هيجلو. لكل تاجر واجهة متجر مستقلة يشاركها مع عملائه، ولوحة لإدارة نشاطه اليومي. تدعم المنصة أنواعًا مختلفة من المنتجات وطرق البيع، بما فيها طلبات الجملة للمحلات والشركات.',
      screenshotAlt: 'موقع سوجو بالعربية بهويته البنفسجية ومعاينة لوحة التاجر وواجهة المتجر',
      screenshotCaption: 'موقع سوجو · معاينة بيئة DEV',
      featuresHeading: 'من كتالوج التاجر إلى طلب العميل.',
      features: [
        { title: 'متجر مستقل لكل تاجر', description: 'رابط خاص يشاركه التاجر مع عملائه، بهويته وتصنيفاته وصفحات منتجاته.' },
        { title: 'المنتجات والمخزون', description: 'إدارة خيارات المنتجات وأسعارها وكمياتها بما يناسب النشاط، من مقاسات الملابس إلى عبوات التموين.' },
        { title: 'للتجزئة والجملة', description: 'إعداد اعتماد حسابات الأعمال والحد الأدنى للطلب وشرائح الأسعار للبيع بالجملة.' },
        { title: 'إدارة المتجر اليومية', description: 'متابعة الطلبات والعملاء والمرتجعات وتقارير المبيعات التشغيلية من لوحة التاجر.' },
      ],
    },
  },
}];

export function localizedPath(path: string, language: Language) {
  const [pathname, hash] = path.split('#');
  if (!pathname || pathname === '/') return `${language === 'ar' ? '/ar/' : '/'}${hash ? `#${hash}` : ''}`;
  if (pathname.startsWith('/case-studies/')) return `${language === 'ar' ? '/ar' : ''}${pathname}${hash ? `#${hash}` : ''}`;
  return `${pathname || '/'}${language === 'ar' ? '?lang=ar' : ''}${hash ? `#${hash}` : ''}`;
}

export const caseCopy = {
  en: {
    label: 'Case studies', title: 'Real businesses. Everyday impact.',
    intro: 'Explore Heglo startups in Sudan: a home-services marketplace and an e-commerce platform for merchants.',
    read: 'Explore Maksab', back: 'All case studies', overview: 'The experience',
    features: 'From finding a service to getting it done.', visit: 'Visit website',
    download: 'Discover the apps', downloadIntro: 'Choose the app for your side of the marketplace.',
    customer: 'Customer app', provider: 'Service provider app', play: 'View on Google Play',
    screenshot: 'Maksab customer app showing maintenance, building, and cleaning services',
    caption: 'Maksab customer app · Google Play',
    next: 'What would you like to build?', nextDescription: 'Tell us about your business and the experience you want to create.',
    contact: 'Talk to Heglo', missing: 'Case study not found', missingDescription: 'Explore our published case studies below.',
  },
  ar: {
    label: 'دراسات الحالة', title: 'أعمال حقيقية. أثر في الحياة اليومية.',
    intro: 'تعرّف على مشاريع مبنية على هيجلو في السودان: سوق للخدمات المنزلية ومنصة للتجارة الإلكترونية للتجّار.',
    read: 'اكتشف مكسب', back: 'جميع دراسات الحالة', overview: 'التجربة',
    features: 'من البحث عن الخدمة إلى إنجازها.', visit: 'زيارة الموقع',
    download: 'اكتشف التطبيقات', downloadIntro: 'اختر التطبيق المناسب لدورك في السوق.',
    customer: 'تطبيق العملاء', provider: 'تطبيق مقدمي الخدمات', play: 'عرض على Google Play',
    screenshot: 'تطبيق مكسب للعملاء يعرض خدمات الصيانة والبناء والنظافة',
    caption: 'تطبيق مكسب للعملاء · Google Play',
    next: 'ما المشروع الذي تريد بناءه؟', nextDescription: 'حدثنا عن عملك والتجربة التي تريد تقديمها لعملائك.',
    contact: 'تواصل مع هيجلو', missing: 'دراسة الحالة غير موجودة', missingDescription: 'تصفّح دراسات الحالة المنشورة أدناه.',
  },
};
